import { useCallback, useMemo } from 'react';
import type { z } from 'zod';
import {
  useGetCartQuery,
  useAddToCartMutation,
  useUpdateCartItemMutation,
  useRemoveFromCartMutation,
  useClearCartMutation,
} from '@/redux/api/cartApi';
import {
  CartListingSchema,
  CartVendorGroupSchema,
  RemovedCartListingSchema,
} from '@gearghost/api-schemas/cart';
import useIsAuthenticated from './useAuth';
import useGuestCart, {
  type GuestCartListing,
  type GuestCartListingInput,
  type GuestVendorGroup,
} from './useGuestCart';

/**
 * Unified cart hook that handles both guest and authenticated users.
 *
 * When logged out: reads from localStorage via useGuestCart.
 * When logged in: reads from the cart API via cartApi.
 *
 * Return shape is a union of the authenticated CartListing and the lighter
 * guest GuestCartListing (and likewise for vendors). Callers using fields
 * only present on one branch must narrow or use defensive access — today
 * `item.listing?.X || item.X` works because the authenticated shape
 * nests `listing` and the guest shape flattens the same fields. This union
 * is a bookmark, not the final shape: the guest cart will be hydrated
 * against a public backend endpoint and returned as full CartSchema, at
 * which point this union collapses. Tracked in
 * project_guest_cart_unified_shape.md.
 */
type UnifiedCartListing = z.infer<typeof CartListingSchema> | GuestCartListing;
type UnifiedCartVendorGroup = z.infer<typeof CartVendorGroupSchema> | GuestVendorGroup;
type RemovedCartListing = z.infer<typeof RemovedCartListingSchema>;

/**
 * Loading-window fallback when the authenticated cart query hasn't
 * resolved yet. This is NOT a silent-fallback against bad data — the
 * schema rejects malformed payloads at the API boundary. It only fires
 * during the brief window between `skip: false` and `isSuccess`, and
 * exists to keep downstream callers simple (always reading fields,
 * never undefined-checking the whole cart object).
 *
 * No cart `id` field is exposed here, by design — cart identity on the
 * wire is the JWT user (see project_checkout_cart_ownership_idor.md).
 */
const EMPTY_CART_LOADING_FALLBACK = {
  listings: [] as UnifiedCartListing[],
  vendors: [] as UnifiedCartVendorGroup[],
  listingCount: 0,
  totalQuantity: 0,
  totalReplacementCost: 0,
  removedListings: [] as RemovedCartListing[],
};

const useCart = () => {
  // Check if user is logged in via authController (reactive)
  const isLoggedIn = useIsAuthenticated();

  // Guest cart (localStorage)
  const guestCart = useGuestCart();

  // Authenticated cart (API) - skip if not logged in
  const {
    data: apiCart,
    isLoading: apiIsLoading,
    isFetching: apiIsFetching,
    refetch: refetchCart,
  } = useGetCartQuery(undefined, {
    skip: !isLoggedIn,
  });

  // API mutations
  const [addToCartApi, { isLoading: isAdding }] = useAddToCartMutation();
  const [updateCartItemApi, { isLoading: isUpdating }] = useUpdateCartItemMutation();
  const [removeFromCartApi, { isLoading: isRemoving }] = useRemoveFromCartMutation();
  const [clearCartApi, { isLoading: isClearing }] = useClearCartMutation();

  // Unified add listing. Accepts GuestCartListingInput so the guest branch
  // can carry denormalized display fields (title, primaryImage, etc.); the
  // auth branch only forwards listingId + quantity to the server.
  const addListing = useCallback(
    async (listing: GuestCartListingInput) => {
      if (isLoggedIn) {
        try {
          await addToCartApi({
            listingId: listing.listingId,
            quantity: listing.quantity || 1,
          }).unwrap();
          return true;
        } catch (err) {
          console.error('Failed to add listing to cart:', err);
          throw err;
        }
      } else {
        guestCart.addListing(listing);
        return true;
      }
    },
    [isLoggedIn, addToCartApi, guestCart]
  );

  // Unified update quantity
  const updateQuantity = useCallback(
    async (listingId: string, quantity: number) => {
      if (isLoggedIn) {
        try {
          await updateCartItemApi({ listingId, quantity }).unwrap();
          return true;
        } catch (err) {
          console.error('Failed to update cart listing:', err);
          throw err;
        }
      } else {
        guestCart.updateQuantity(listingId, quantity);
        return true;
      }
    },
    [isLoggedIn, updateCartItemApi, guestCart]
  );

  // Unified remove listing
  const removeListing = useCallback(
    async (listingId: string) => {
      if (isLoggedIn) {
        try {
          await removeFromCartApi(listingId).unwrap();
          return true;
        } catch (err) {
          console.error('Failed to remove listing from cart:', err);
          throw err;
        }
      } else {
        guestCart.removeListing(listingId);
        return true;
      }
    },
    [isLoggedIn, removeFromCartApi, guestCart]
  );

  // Unified clear cart
  const clearCart = useCallback(async () => {
    if (isLoggedIn) {
      try {
        await clearCartApi().unwrap();
        return true;
      } catch (err) {
        console.error('Failed to clear cart:', err);
        throw err;
      }
    } else {
      guestCart.clearCart();
      return true;
    }
  }, [isLoggedIn, clearCartApi, guestCart]);

  // Unified cart data.
  //
  // Authenticated branch: apiCart is CartSchema-typed (thanks to tier A/B).
  // During the RTK Query loading window apiCart is undefined, so we fall
  // through to EMPTY_CART_LOADING_FALLBACK — see that constant's comment.
  //
  // Guest branch: compose GuestCartListing / GuestVendorGroup into the
  // same shape. No `id` is surfaced — cart identity on the wire is the
  // JWT user (see project_checkout_cart_ownership_idor.md).
  const cartData = useMemo<{
    listings: UnifiedCartListing[];
    vendors: UnifiedCartVendorGroup[];
    listingCount: number;
    totalQuantity: number;
    totalReplacementCost: number;
    removedListings: RemovedCartListing[];
  }>(() => {
    if (isLoggedIn) {
      return apiCart ?? EMPTY_CART_LOADING_FALLBACK;
    }

    return {
      listings: guestCart.listings,
      vendors: guestCart.vendors,
      listingCount: guestCart.listingCount,
      totalQuantity: guestCart.totalQuantity,
      // Guest lines carry no replacement data; nothing guest-facing
      // renders this value (the summary card is authed-only).
      totalReplacementCost: 0,
      // A guest cart lives in the browser; the server judges its lines when
      // they sync at login, and drops any that can no longer be rented.
      removedListings: [],
    };
  }, [isLoggedIn, apiCart, guestCart]);

  // Loading state
  const isLoading = useMemo(() => {
    if (isLoggedIn) {
      return apiIsLoading;
    }
    return !guestCart.isLoaded;
  }, [isLoggedIn, apiIsLoading, guestCart.isLoaded]);

  // Is any mutation in progress
  const isMutating = isAdding || isUpdating || isRemoving || isClearing;

  return {
    // State from cartData
    listings: cartData.listings,
    vendors: cartData.vendors,
    listingCount: cartData.listingCount,
    totalQuantity: cartData.totalQuantity,
    totalReplacementCost: cartData.totalReplacementCost,
    // Lines that left the cart (their listing can no longer be rented),
    // named until the renter clears them through removeListing.
    removedListings: cartData.removedListings,
    // The date prompt's later return days with the window they answer for:
    // the cart's own dates, so a cart read for older dates never matches the
    // dates just picked. A guest cart lives in the browser and has none;
    // listing detail offers them through its quote.
    laterReturn:
      isLoggedIn && apiCart
        ? {
            pickupDate: apiCart.pickupDate,
            returnDate: apiCart.returnDate,
            options: apiCart.laterReturnOptions,
          }
        : null,
    // Why checkout waits on the renter's ID, in checkout's words, or null
    // once it is verified (Tyler, 2026-10-23). A guest has none: their
    // Checkout press goes to log in first, and the cart they sign in to
    // carries their own.
    idCheckoutMessage: isLoggedIn && apiCart ? apiCart.idCheckoutMessage : null,

    // Loading states
    isLoading,
    isFetching: isLoggedIn ? apiIsFetching : false,
    isMutating,
    isLoggedIn,

    // Computed
    isEmpty: cartData.listings.length === 0,

    // Methods
    addListing,
    updateQuantity,
    removeListing,
    clearCart,
    refetch: isLoggedIn ? refetchCart : () => {},

    // Guest cart for sync on login
    getGuestCartForSync: guestCart.getCartForSync,
    clearGuestCart: guestCart.clearCart,
  };
};

export default useCart;
